/**
 * Datadog User Context Integration Component
 *
 * This component integrates with the application's authentication system to
 * automatically set user context in Datadog RUM when users log in or out.
 *
 * It watches for changes in authentication state and updates Datadog's user
 * context accordingly, enabling you to:
 * - Segment performance data by user type (anonymous, customer, organizer, staff)
 * - Track issues affecting specific users
 * - Analyze performance differences between user segments
 *
 * Usage:
 * This component is automatically rendered in _app.tsx and requires no additional setup.
 */

import React, { useEffect } from "react";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { EnvFactory } from "@shared/env/EnvFactory";
import { DD_RUM_ENABLED } from "@shared/env";
import {
  clearUserContext,
  setUserContext,
} from "../../utils/datadog/datadog-timings";

/**
 * DatadogUserContext component manages user context in Datadog RUM
 */
const DatadogUserContext: React.FC = () => {
  const { isLoggedIn, userProfileData } = useUserSessionAuthState();

  // Update user context whenever authentication state changes
  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") {
      return;
    }

    // Check if RUM is enabled
    if (!DD_RUM_ENABLED) {
      return;
    }

    try {
      if (isLoggedIn && userProfileData) {
        // User is logged in - set user context
        setUserContext({
          id: userProfileData.id,
          email: userProfileData.email,
          name:
            `${userProfileData.first_name || ""} ${
              userProfileData.last_name || ""
            }`.trim() || undefined,
        });
      } else {
        // User is logged out or data not available - clear user context
        clearUserContext();

        // Log context clear in development
        if (EnvFactory.getValue("DD_ENV") === "preview") {
          // eslint-disable-next-line no-console
          console.info("[Datadog RUM] User context cleared");
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to update Datadog user context:", error);
    }
  }, [isLoggedIn, userProfileData]);

  return null;
};

export default DatadogUserContext;
